package dev.kotlinfest._2026.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.SiteLayout
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.components.layouts.PageLayoutData
import dev.kotlinfest._2026.desktopPageHorizontalPadding
import dev.kotlinfest._2026.models.UserSession
import dev.kotlinfest._2026.models.UserSessions
import dev.kotlinfest._2026.sessionVenueColor
import dev.kotlinfest._2026.sitePageHero
import org.jetbrains.compose.web.css.*

private val _detailLinkVariant = UndecoratedLinkVariant.then(UncoloredLinkVariant)

val SessionHeaderBackdropStyle = CssStyle {
    base { Modifier.sitePageHero(80) }
    Breakpoint.MD { Modifier.sitePageHero(160) }
}

val SessionDetailContentStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(top = 4.cssRem, bottom = 3.cssRem, leftRight = SiteLayout.MobileMarginPx.px)
            .styleModifier { property("box-sizing", "border-box") }
    }
    Breakpoint.MD {
        Modifier
            .maxWidth(SiteLayout.DesktopDesignWidthPx.px)
            .padding(top = 7.5.cssRem, bottom = 10.cssRem)
            .desktopPageHorizontalPadding()
            .styleModifier {
                property("margin-left", "auto")
                property("margin-right", "auto")
            }
    }
}

val SessionLeadStyle = CssStyle.base { Modifier.fillMaxWidth().gap(0.75.cssRem) }

val SessionMetaStyle = CssStyle.base {
    Modifier.gap(0.85.cssRem).fontSize(0.78.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.brand.ink)
}

val SessionDetailTitleStyle = CssStyle {
    base { Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.55).letterSpacing(0.03.cssRem).color(SitePalette.brand.ink) }
    Breakpoint.MD { Modifier.fontSize(1.65.cssRem).letterSpacing(0.08.cssRem) }
}

val SpeakerProfileStyle = CssStyle.base {
    Modifier.gap(0.65.cssRem).alignItems(AlignItems.Center)
}

val SpeakerAvatarStyle = CssStyle.base {
    Modifier
        .width(2.35.cssRem)
        .height(2.35.cssRem)
        .borderRadius(50.percent)
        .backgroundColor(SitePalette.brand.border)
        .styleModifier { property("object-fit", "cover") }
}

val SpeakerNameStyle = CssStyle.base {
    Modifier.fontSize(0.7.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.5).color(SitePalette.brand.ink)
}

val SpeakerAccountStyle = CssStyle.base {
    Modifier.fontSize(0.58.cssRem).letterSpacing(0.04.cssRem).color(SitePalette.brand.ink)
}

val SessionDescriptionStyle = CssStyle {
    base { Modifier.margin(top = 1.6.cssRem).gap(1.05.cssRem).fontSize(0.72.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.75).color(SitePalette.brand.ink) }
    Breakpoint.MD { Modifier.margin(top = 3.cssRem).fontSize(0.78.cssRem).lineHeight(1.65) }
}

val RelatedSectionStyle = CssStyle {
    base { Modifier.fillMaxWidth().margin(top = 3.5.cssRem).gap(1.cssRem) }
    Breakpoint.MD { Modifier.margin(top = 3.75.cssRem) }
}

val DesktopOnlyRelatedStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .margin(top = 3.75.cssRem)
            .gap(1.cssRem)
    }
}

val RelatedHeadingStyle = CssStyle {
    base { Modifier.fontSize(1.12.cssRem).fontWeight(FontWeight.Bold).letterSpacing(0.04.cssRem).color(SitePalette.brand.ink) }
    Breakpoint.MD { Modifier.fontSize(1.3.cssRem) }
}

val RelatedCardStyle = CssStyle.base {
    Modifier.fillMaxWidth().padding(1.25.cssRem).gap(0.5.cssRem)
        .border(1.5.px, LineStyle.Solid, SitePalette.brand.pink).borderRadius(0.85.cssRem)
        .styleModifier { property("box-sizing", "border-box") }
}

val RelatedTitleStyle = CssStyle {
    base { Modifier.fontSize(0.95.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.5).letterSpacing(0.04.cssRem).color(SitePalette.brand.pink) }
    Breakpoint.MD { Modifier.fontSize(1.03.cssRem) }
}

val EmptyRelatedStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(topBottom = 1.5.cssRem, leftRight = 1.25.cssRem)
        .border(1.px, LineStyle.Solid, SitePalette.brand.border)
        .borderRadius(0.85.cssRem)
        .backgroundColor(SitePalette.nearBackground)
        .fontSize(0.75.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(SitePalette.brand.muted)
        .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
}

@Composable
private fun Tags(session: UserSession, showVenue: Boolean = false) {
    Row(Modifier.gap(0.35.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
        SpanText(session.category, TagStyle.toModifier())
        session.level?.let { SpanText(it, TagStyle.toModifier()) }
        if (showVenue) {
            SpanText(session.venue, TagStyle.toModifier().backgroundColor(sessionVenueColor(session.venue)))
        }
    }
}

@Composable
private fun RelatedCard(session: UserSession) {
    Link("/sessions/${session.id}", modifier = Modifier.fillMaxWidth(), variant = _detailLinkVariant) {
        Column(RelatedCardStyle.toModifier()) {
            SpanText(session.time, SessionTimeStyle.toModifier())
            Tags(session, showVenue = true)
            SpanText(session.title, RelatedTitleStyle.toModifier())
            session.affiliation?.let { SpanText(it, SpeakerStyle.toModifier()) }
            SpanText(session.speaker, SpeakerStyle.toModifier())
        }
    }
}

@Composable
private fun RelatedCards(sessions: List<UserSession>) {
    if (sessions.isEmpty()) {
        SpanText("該当するセッションはありません", EmptyRelatedStyle.toModifier())
    } else {
        sessions.take(3).forEach { RelatedCard(it) }
    }
}

@InitRoute
fun initSessionDetailPage(ctx: InitRouteContext) {
    val session = UserSessions.find(ctx.route.params["id"])
    ctx.data.add(PageLayoutData(session?.title ?: "セッション詳細", fullWidth = true))
}

@Page("/sessions/{id}")
@Layout(".components.layouts.PageLayout")
@Composable
fun SessionDetailPage(ctx: PageContext) {
    val session = UserSessions.find(ctx.route.params["id"])
    if (session == null) {
        Column(Modifier.fillMaxWidth()) {
            Column(SessionHeaderBackdropStyle.toModifier()) {}
            Column(SessionDetailContentStyle.toModifier()) {
                SpanText("セッションが見つかりません", SessionDetailTitleStyle.toModifier())
                Link("/timetable", "タイムテーブルへ戻る")
            }
        }
        return
    }
    val sameTimeSessions = UserSessions.all.filter { it.id != session.id && it.time == session.time }
    val sameCategorySessions = UserSessions.all.filter { it.id != session.id && it.category == session.category }

    Column(Modifier.fillMaxWidth()) {
        Column(SessionHeaderBackdropStyle.toModifier()) {}
        Column(SessionDetailContentStyle.toModifier()) {
            Column(SessionLeadStyle.toModifier()) {
                Row(SessionMetaStyle.toModifier()) {
                    SpanText(session.time)
                    SpanText(session.venue)
                }
                Tags(session)
                SpanText(session.title, SessionDetailTitleStyle.toModifier())
                Row(SpeakerProfileStyle.toModifier()) {
                    Image(session.avatarPath, session.speaker, SpeakerAvatarStyle.toModifier())
                    Link(
                        "https://x.com/${session.account.removePrefix("@")}",
                        modifier = Modifier.attr("target", "_blank").attr("rel", "noopener noreferrer"),
                        variant = _detailLinkVariant,
                    ) {
                        Column {
                            SpanText(session.speaker, SpeakerNameStyle.toModifier())
                            session.affiliation?.let { SpanText(it, SpeakerAccountStyle.toModifier()) }
                            SpanText(session.account, SpeakerAccountStyle.toModifier())
                        }
                    }
                }
            }

            Column(SessionDescriptionStyle.toModifier()) {
                session.description.forEach { SpanText(it) }
                session.targetAudience?.let { SpanText("想定聴講者: $it") }
            }

            if (session.level != null) {
                Column(DesktopOnlyRelatedStyle.toModifier()) {
                    SpanText("同じ時間のセッション", RelatedHeadingStyle.toModifier())
                    RelatedCards(sameTimeSessions)
                }
                Column(RelatedSectionStyle.toModifier()) {
                    SpanText("同カテゴリーのセッション", RelatedHeadingStyle.toModifier())
                    RelatedCards(sameCategorySessions)
                }
            }
        }
    }
}
